import React, { useEffect, useState, useMemo } from 'react';
import { Button, Form, Header, Modal, Segment } from 'semantic-ui-react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, isRoot, showError, showSuccess } from '../../helpers';
import { renderQuota, renderQuotaWithPrompt } from '../../helpers/render';

const invoiceTypeOptions = [
  { key: 1, text: '普通发票', value: 1 },
  { key: 2, text: '增值税专用发票', value: 2 }
];

const EditUser = () => {
  const params = useParams();
  const userId = params.id;
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({
    username: '',
    display_name: '',
    password: '',
    password_confirm: '',
    wechat_id: '',
    email: '',
    quota: 0,
    group: 'default',
    remark: '',
    video_task_limit: 10,
    max_token_count: 50,
    credit_quota_yuan: 0,
    role: 1,
    admin_tenant: ''
  });
  const [groupOptions, setGroupOptions] = useState([]);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceProfile, setInvoiceProfile] = useState({
    type: 1,
    title: '',
    tax_number: ''
  });

  // 角色由 root 在用户编辑页设置，10 = 租户管理员（只读、绑定单租户），20 = 市场（全量只读）
  const roleOptions = [
    { key: 1, text: '普通用户', value: 1 },
    { key: 10, text: '租户管理员', value: 10 },
    { key: 20, text: '市场', value: 20 },
    { key: 100, text: '超级管理员', value: 100 }
  ];
  const quotaPerUnit = useMemo(() => {
    let v = parseFloat(localStorage.getItem('quota_per_unit'));
    if (!v || v <= 0) v = 500000;
    return v;
  }, []);
  const { username, display_name, password, wechat_id, email, quota, group } =
    inputs;
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };
  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(res.data.data.map((group) => ({
        key: group,
        text: group,
        value: group,
      })));
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchTenants = async () => {
    try {
      let res = await API.get(`/api/tenant/`);
      setTenantOptions((res.data.data || []).map((t) => ({
        key: t.key,
        text: t.name ? `${t.name} (${t.key})` : t.key,
        value: t.key
      })));
    } catch (error) {
      showError(error.message);
    }
  };
  const navigate = useNavigate();
  const handleCancel = () => {
    navigate("/setting");
  }
  const loadUser = async () => {
    let res = undefined;
    if (userId) {
      res = await API.get(`/api/user/${userId}`);
    } else {
      res = await API.get(`/api/user/self`);
    }
    const { success, message, data } = res.data;
    if (success) {
      data.password = '';
      data.credit_quota_yuan = data.credit_quota ? Math.abs(data.credit_quota) / quotaPerUnit : 0;
      setInputs(data);
    } else {
      showError(message);
    }
    setLoading(false);
  };
  useEffect(() => {
    loadUser().then();
    if (userId) {
      fetchGroups().then();
    }
    if (userId && isRoot()) {
      fetchTenants().then();
    }
  }, []);

  const submit = async () => {
    let res = undefined;
    if (userId) {
      let data = { ...inputs, id: parseInt(userId) };
      // 额度不在此处修改，不提交
      delete data.quota;
      data.role = parseInt(data.role) || 1;
      if (data.role !== 10) {
        data.admin_tenant = '';
      }
      if (typeof data.video_task_limit === 'string') {
        data.video_task_limit = parseInt(data.video_task_limit);
      }
      if (typeof data.max_token_count === 'string') {
        data.max_token_count = parseInt(data.max_token_count) || 0;
      }
      let creditYuan = parseFloat(data.credit_quota_yuan) || 0;
      data.credit_quota = -Math.round(creditYuan * quotaPerUnit);
      delete data.credit_quota_yuan;
      res = await API.put(`/api/user/`, data);
    } else {
      if (inputs.password !== inputs.password_confirm) {
        showError('两次输入的密码不一致！');
        return;
      }
      res = await API.put(`/api/user/self`, { password: inputs.password });
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(userId ? '用户信息更新成功！' : '密码更新成功！');
      if (!userId) {
        navigate('/setting');
      }
    } else {
      showError(message);
    }
  };

  const openInvoiceModal = async () => {
    setInvoiceProfile({ type: 1, title: '', tax_number: '' });
    setInvoiceModalOpen(true);
    setInvoiceLoading(true);
    try {
      const res = await API.get(`/api/invoice/admin/profile/${userId}`);
      const { success, message, data } = res.data;
      if (success && data) {
        setInvoiceProfile({
          type: data.type || 1,
          title: data.title || '',
          tax_number: data.tax_number || ''
        });
      } else if (!success) {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleInvoiceProfileChange = (e, { name, value }) => {
    setInvoiceProfile((profile) => ({ ...profile, [name]: value }));
  };

  const saveInvoiceProfile = async () => {
    const title = invoiceProfile.title.trim();
    const taxNumber = invoiceProfile.tax_number.trim();
    if (!title || !taxNumber) {
      showError('发票抬头和税号不能为空');
      return;
    }
    setInvoiceSaving(true);
    try {
      const res = await API.put(`/api/invoice/admin/profile/${userId}`, {
        type: parseInt(invoiceProfile.type) || 1,
        title,
        tax_number: taxNumber
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess('发票信息已保存');
        setInvoiceModalOpen(false);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setInvoiceSaving(false);
    }
  };

  return (
    <>
      <Segment loading={loading}>
        <Header as='h3'>{userId ? '更新用户信息' : '更新密码'}</Header>
        {userId && isRoot() && (
          <Button
            type='button'
            size='small'
            onClick={openInvoiceModal}
            style={{ marginBottom: '1em' }}
          >
            编辑发票信息
          </Button>
        )}
        <Form autoComplete='new-password'>
          {userId && (
            <Form.Field>
              <Form.Input
                label='用户名'
                name='username'
                placeholder={'请输入新的用户名'}
                onChange={handleInputChange}
                value={username}
                autoComplete='new-password'
              />
            </Form.Field>
          )}
          <Form.Field>
            <Form.Input
              label='密码'
              name='password'
              type={'password'}
              placeholder={'请输入新的密码，最短 8 位'}
              onChange={handleInputChange}
              value={password}
              autoComplete='new-password'
            />
          </Form.Field>
          {!userId && (
            <Form.Field>
              <Form.Input
                label='确认密码'
                name='password_confirm'
                type={'password'}
                placeholder={'请再次输入新的密码'}
                onChange={handleInputChange}
                value={inputs.password_confirm}
                autoComplete='new-password'
              />
            </Form.Field>
          )}
          {userId && (
            <Form.Field>
              <Form.Input
                label='显示名称'
                name='display_name'
                placeholder={'请输入新的显示名称'}
                onChange={handleInputChange}
                value={display_name}
                autoComplete='new-password'
              />
            </Form.Field>
          )}
          {
            userId && <>
              <Form.Field>
                <Form.Dropdown
                  label='分组'
                  placeholder={'请选择分组'}
                  name='group'
                  fluid
                  search
                  selection
                  allowAdditions
                  additionLabel={'请在系统设置页面编辑分组倍率以添加新的分组：'}
                  onChange={handleInputChange}
                  value={inputs.group}
                  autoComplete='new-password'
                  options={groupOptions}
                />
              </Form.Field>
              <Form.Field>
                <Form.Input
                  label={`剩余额度${renderQuotaWithPrompt(quota)}`}
                  name='quota'
                  placeholder={'请输入新的剩余额度'}
                  onChange={handleInputChange}
                  value={quota}
                  type={'number'}
                  disabled
                  autoComplete='new-password'
                />
              </Form.Field>
              <Form.Field>
                <Form.Input
                  label='视频任务并发限制'
                  name='video_task_limit'
                  placeholder='默认 10'
                  onChange={handleInputChange}
                  value={inputs.video_task_limit}
                  type='number'
                  min={1}
                />
              </Form.Field>
              <Form.Field>
                <Form.Input
                  label='信用额度（元）'
                  name='credit_quota_yuan'
                  placeholder='0 表示无信用额度'
                  onChange={handleInputChange}
                  value={inputs.credit_quota_yuan}
                  type='number'
                  min={0}
                  step='0.01'
                />
              </Form.Field>
            </>
          }
          {userId && (
            <Form.Field>
              <Form.Input
                label='已绑定的邮箱账户'
                name='email'
                value={email}
                autoComplete='new-password'
                placeholder='此项只读，需要用户通过个人设置页面的相关绑定按钮进行绑定，不可直接修改'
                readOnly
              />
            </Form.Field>
          )}
          {userId && isRoot() && (
            <>
              <Form.Field>
                <Form.Dropdown
                  label='用户角色'
                  name='role'
                  selection
                  options={roleOptions}
                  value={inputs.role}
                  onChange={handleInputChange}
                />
              </Form.Field>
              {parseInt(inputs.role) === 10 && (
                <Form.Field>
                  <Form.Dropdown
                    label='绑定租户'
                    name='admin_tenant'
                    selection
                    clearable
                    placeholder='请选择租户'
                    options={tenantOptions}
                    value={inputs.admin_tenant}
                    onChange={handleInputChange}
                  />
                </Form.Field>
              )}
              <Form.Field>
                <Form.Input
                  label='最大令牌数'
                  name='max_token_count'
                  placeholder='默认 50'
                  onChange={handleInputChange}
                  value={inputs.max_token_count}
                  type='number'
                  min={0}
                />
              </Form.Field>
            </>
          )}
          {userId && isRoot() && (
            <Form.Field>
              <Form.TextArea
                label='备注'
                name='remark'
                placeholder='仅超级管理员可见'
                onChange={handleInputChange}
                value={inputs.remark}
                rows={3}
              />
            </Form.Field>
          )}
          <Button onClick={handleCancel}>取消</Button>
          <Button positive onClick={submit}>提交</Button>
        </Form>
      </Segment>
      {userId && isRoot() && (
        <Modal
          open={invoiceModalOpen}
          onClose={() => setInvoiceModalOpen(false)}
          size='small'
        >
          <Modal.Header>编辑用户发票信息</Modal.Header>
          <Modal.Content>
            <Form loading={invoiceLoading}>
              <Form.Field>
                <label>发票类型</label>
                <Form.Select
                  name='type'
                  options={invoiceTypeOptions}
                  value={invoiceProfile.type}
                  onChange={handleInvoiceProfileChange}
                />
              </Form.Field>
              <Form.Field>
                <label>发票抬头</label>
                <Form.Input
                  name='title'
                  placeholder='公司名称'
                  value={invoiceProfile.title}
                  onChange={handleInvoiceProfileChange}
                />
              </Form.Field>
              <Form.Field>
                <label>纳税人识别号</label>
                <Form.Input
                  name='tax_number'
                  placeholder='税号'
                  value={invoiceProfile.tax_number}
                  onChange={handleInvoiceProfileChange}
                />
              </Form.Field>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button
              type='button'
              onClick={() => setInvoiceModalOpen(false)}
              disabled={invoiceSaving}
            >
              取消
            </Button>
            <Button
              type='button'
              positive
              onClick={saveInvoiceProfile}
              loading={invoiceSaving}
              disabled={invoiceLoading || invoiceSaving}
            >
              保存
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};

export default EditUser;
